import { Link, useNavigate } from "react-router-dom";
import { toUrl } from "./routes";
import { todayApi, type TodayDue } from "../lib/api";
import { useCompany } from "../masters/reference";
import { useLoad } from "../ui/useLoad";
import { ErrorNote } from "../ui/bits";
import { fmtAmount } from "../lib/money";
import { ALL_PAGES } from "./nav";
import { SectionCard } from "./Shell";
import { SECTIONS } from "./nav";
import { docTitle } from "../vouchers/docs";
import { GettingStarted } from "./GettingStarted";
import { HelpButton } from "../help/HelpPanel";

const QUICK: [string, string][] = [["SI01", "Invoice"], ["PB01", "Bill"], ["RC01", "Receipt"], ["PY01", "Payment"], ["JE01", "Journal"], ["BK01", "Reconcile"]];
const rupees = (n: number | undefined) => `₹ ${fmtAmount(Math.round(n ?? 0))}`.replace(/\.00$/, "");
const STATE: Record<TodayDue["state"], string> = { filed: "Filed", overdue: "Overdue", due: "Due", ready: "Ready to file", upcoming: "Coming up" };
const day = (iso: string) => new Date(iso + "T00:00:00");

// A company's home: the figures, what needs someone, the returns and payments around today, and the latest entries.
export function Today() {
  const { company } = useCompany();
  const nav = useNavigate();
  const { data, error } = useLoad(() => todayApi.get(company.id), [company.id]);
  const today = new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
  const q = data?.queues ?? [];
  const nextDue = data?.calendar.find((c) => c.state !== "filed" && c.state !== "upcoming") ?? data?.calendar.find((c) => c.state === "upcoming");
  const daysTo = nextDue ? Math.round((day(nextDue.date).getTime() - day(data!.as_on).getTime()) / 86400000) : null;
  const f = data?.figures;

  return (
    <section className="page today">
      <div className="today-head">
        <div>
          <h1>Today, {today}</h1>
          <p className="muted">{!data ? "Working out what needs you…"
            : `${q.length ? `${q.length} thing${q.length > 1 ? "s" : ""} need${q.length > 1 ? "" : "s"} you` : "Nothing needs you right now"}${nextDue && daysTo != null
              ? `${q.length ? ", and" : "."} ${nextDue.title} ${daysTo < 0 ? `was due ${-daysTo} day${daysTo === -1 ? "" : "s"} ago` : daysTo === 0 ? "is due today" : `is due in ${daysTo} day${daysTo === 1 ? "" : "s"}`}.` : "."}`}</p>
        </div>
        <div className="quick"><HelpButton code="HM01" title="Today" />{QUICK.map(([c, name]) => { const p = ALL_PAGES.find((x) => x.code === c)!; return (
          <Link key={c} to={toUrl(`${p.path}`)} className="chip"><span className="code">{c}</span>{name}</Link>); })}</div>
      </div>
      <ErrorNote text={error?.message} />
      <GettingStarted />

      <div className="kpis">
        <div className="card kpi"><div className="kpi-label">Cash and bank</div><div className="kpi-value">{rupees(f?.cash_bank)}</div>
          <div className="kpi-sub muted">In the books today</div></div>
        <div className="card kpi"><div className="kpi-label">Customers owe</div><div className="kpi-value">{rupees(f?.receivables)}</div>
          <div className="kpi-sub muted">{f?.receivables_overdue ? `${rupees(f.receivables_overdue)} overdue past 30 days` : "Nothing overdue past 30 days"}</div></div>
        <div className="card kpi"><div className="kpi-label">You owe suppliers</div><div className="kpi-value">{rupees(f?.payables)}</div>
          <div className="kpi-sub muted">{f?.payables_due_week ? `${rupees(f.payables_due_week)} due within a week` : "Nothing due within a week"}</div></div>
        {f?.gst_registered && <div className="card kpi"><div className="kpi-label">GST this month</div><div className="kpi-value">{rupees(Math.abs(f.gst_month ?? 0))}</div>
          <div className="kpi-sub muted">{(f.gst_month ?? 0) >= 0 ? "Output tax less input tax so far" : "Input tax ahead of output tax so far"}</div></div>}
      </div>

      <div className="today-cols">
        <section className="card today-box grow3">
          <header><h2>Needs attention</h2><span className="muted small">Worked out from the books</span></header>
          {!data ? <p className="muted pad">…</p> : q.length === 0 ? <p className="muted pad">All clear: no bank lines to match, no drafts, nothing overdue.</p>
            : q.map((x) => (
              <div key={x.key} className="todo">
                <div className={`count tone-${x.tone}`}>{x.count}</div>
                <div className="what"><div className="t">{x.title}</div><div className="muted small">{x.detail}</div></div>
                <Link className="chip" to={toUrl(`${x.path}`)}>{x.action}<span className="code">{x.code}</span></Link>
              </div>))}
        </section>
        <section className="card today-box grow2">
          <header><h2>Compliance calendar</h2><span className="muted small">Due dates as the law sets them</span></header>
          {!data ? <p className="muted pad">…</p> : data.calendar.length === 0
            ? <p className="muted pad">Add a GSTIN or TDS details in Settings (ST01) and the returns show here.</p>
            : data.calendar.map((c, i) => (
              <Link key={i} to={toUrl(`${c.path}`)} className="due">
                <div className="date"><b>{day(c.date).getDate()}</b><span>{day(c.date).toLocaleDateString("en-IN", { month: "short" })}</span></div>
                <div className="what"><div className="t">{c.title}</div><div className="muted small">{c.detail}</div></div>
                <span className={`pill state-${c.state}`}>{c.state === "filed" && c.filed_on ? `Filed ${day(c.filed_on).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : STATE[c.state]}</span>
              </Link>))}
        </section>
      </div>

      <section className="card today-box">
        <header><h2>Recent transactions</h2><Link className="small" to={toUrl(`vouchers/daybook`)}>Register <span className="code">JR01</span></Link></header>
        {data && data.recent.length === 0 ? <p className="muted pad">Nothing entered yet. Type SI01 for a sales invoice, PB01 for a purchase bill.</p> :
        <div className="scroll-x"><table className="grid-table">
          <thead><tr><th>Date</th><th>Number</th><th>Kind</th><th>Party or note</th><th className="num">Amount ₹</th></tr></thead>
          <tbody>{(data?.recent ?? []).map((r) => (
            <tr key={r.id} className="link-row" onClick={() => nav(toUrl(`vouchers/v/${r.id}`))}>
              <td>{day(r.date).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td>
              <td className="mono">{r.number ?? <span className="tag">draft</span>}</td>
              <td>{docTitle({ name: r.type, base_type: r.base_type })}</td><td>{r.party ?? ""}</td>
              <td className="num">{fmtAmount(r.amount)}</td></tr>))}</tbody>
        </table></div>}
      </section>

      <details className="all-codes">
        <summary>Every page and its code</summary>
        <div className="grid codes-grid">{SECTIONS.map((s) => <SectionCard key={s.id} id={s.id} />)}</div>
      </details>
    </section>
  );
}
